import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, viewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { SuiTooltipDirective } from '@semeris/ui';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { AppSelectors } from 'projects/semeris/src/store/app/app.selectors';
import { ChecklistActions } from 'projects/semeris/src/store/checklist/checklist.actions';
import { DealComparisonActions } from 'projects/semeris/src/store/deal-comparison/deal-comparison.actions';
import { DealComparisonSelectors } from 'projects/semeris/src/store/deal-comparison/deal-comparison.selectors';
import { DocumentViewerSelectors } from 'projects/semeris/src/store/document-viewer/document-viewer.selectors';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { SearchActions } from 'projects/semeris/src/store/search/search.actions';
import { SearchSelectors } from 'projects/semeris/src/store/search/search.selectors';
import { TransactionSelectors } from 'projects/semeris/src/store/transaction/transaction.selectors';
import { UserProfileActions } from 'projects/semeris/src/store/user-profile/user-profile.actions';
import { UserProfileSelectors } from 'projects/semeris/src/store/user-profile/user-profile.selectors';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';

import { DocumentViewerActions } from '../../../../store/document-viewer/document-viewer.actions';
import { AdminService } from '../../../admin/services/admin.service';
import { UserInfo } from '../../../authentication/models/user-info.model';
import { DealFilterMode } from '../../../deal-filter/models/deal-filter.model';
import { XRayControlComponent } from '../../../document-viewer/components/xray-control/xray-control.component';
import { XRayOptions, XRayOptionsChangedEvent } from '../../../document-viewer/models/document-viewer.models';
import { FEEDBACK_TEMPLATE_MAP } from '../../../feedback/components/feedback-modal/feedback-modal.component';
import { HelpIconComponent } from '../../../help/components/help-icon/help-icon.component';
import { HelpComponentMode } from '../../../help/models/help.model';
import { Transaction } from '../../../home/models/transaction.model';
import { ApplicationService } from '../../../home/services/application.service';
import { DEFAULT_COLUMN } from '../../../market-comparison/constants/default-column';
import { Column, TopicId, TransactionId } from '../../../market-comparison/models/column.model';
import { SearchBoxComponent } from '../../../search/components/search-box/search-box.component';
import { SearchResult, UnifiedSearchResult } from '../../../search/models/search.model';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { DealsIndicatorComponent } from '../../../shared/components/deals-indicator/deals-indicator.component';
import { DefaultLabelSelectorComponent } from '../../../shared/components/default-label-selector/default-label-selector.component';
import { NotSupportedBrowserIndicatorComponent } from '../../../shared/components/not-supported-browser-indicator/not-supported-browser-indicator.component';
import { WarningChipComponent } from '../../../shared/components/warning-chip/warning-chip.component';
import { pushToRecord } from '../../../shared/constants/record-helper';
import { SemerisRoutes } from '../../../shared/constants/routes';
import { ConfirmDirective } from '../../../shared/directives/confirm.directive';
import { FastTooltipDirective } from '../../../shared/directives/fast-tooltip.directive';
import { HasAssetDirective } from '../../../shared/directives/has-asset.directive';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { PanelTarget } from '../../../shared/models/common/command';
import { IPanelComponent } from '../../../shared/models/common/i-panel.component';
import { TenantNameResolverPipe } from '../../../shared/pipes/tenant-name-resovler.pipe';
import { TransactionToLinkPipe } from '../../../shared/pipes/transaction-to-link.pipe';
import { UserNameResolverPipe } from '../../../shared/pipes/user-name-resovler.pipe';
import { DeploymentSwitchService } from '../../../shared/services/deployment/deployment-switch.service';
import { type DeploymentVariant, VARIANT_LABELS } from '../../../shared/services/deployment/deployment-variant';
import { LocalStorageService } from '../../../shared/services/local-storage.service';
import { PermissionService } from '../../../shared/services/permission.service';
import { flattenObject } from '../../../shared/utils/object.helpers';
import { ButtonDirective } from '../../../ui/button/button/button.directive';
import { IconButtonDirective } from '../../../ui/button/icon-button/icon-button.directive';
import { ConnectionStatusIndicatorComponent } from '../connection-status-indicator/connection-status-indicator.component';
import { ManagerApprovalDropdownComponent } from '../manager-approval-dropdown/manager-approval-dropdown.component';
import { OrphanDataCleanerModalComponent } from '../orphan-data-cleaner-modal/orphan-data-cleaner-modal.component';

interface DeploymentOption {
  variant: DeploymentVariant;
  label: string;
  tooltip: string;
}

const DEPLOYMENT_OPTIONS: readonly DeploymentOption[] = [
  { variant: 'classic', label: VARIANT_LABELS.classic, tooltip: 'The current interface' },
  { variant: 'next', label: VARIANT_LABELS.next, tooltip: 'The new interface — same data' },
];

const DEPLOYMENT_INERT_TOOLTIP = 'Switching is unavailable on localhost';

@Component({
  imports: [
    CommonModule,
    PermissionsDirective,
    XRayControlComponent,
    HasAssetDirective,
    MatIconModule,
    RouterModule,
    MatMenuModule,
    MatDividerModule,
    AvatarComponent,
    SearchBoxComponent,
    NotSupportedBrowserIndicatorComponent,
    DealsIndicatorComponent,
    WarningChipComponent,
    TenantNameResolverPipe,
    UserNameResolverPipe,
    HelpIconComponent,
    DefaultLabelSelectorComponent,
    FastTooltipDirective,
    ConfirmDirective,
    ButtonDirective,
    IconButtonDirective,
    ManagerApprovalDropdownComponent,
    ConnectionStatusIndicatorComponent,
    SuiTooltipDirective,
  ],
  selector: 'semeris-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ToolbarComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly authService = inject(AuthService);
  private readonly applicationService = inject(ApplicationService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);
  private readonly adminService = inject(AdminService);
  private readonly dialog = inject(MatDialog);
  private readonly deploymentSwitch = inject(DeploymentSwitchService);

  public userInfo$: Observable<UserInfo>;
  public Permissions = Permission;

  /* ── Interface switch (classic ⇄ next) ─────────────────── */

  public readonly deploymentVariant = this.deploymentSwitch.variant;

  /** Rendered on localhost too, where it shows this build's side but cannot navigate. */
  public readonly showDeploymentToggle = this.deploymentSwitch.isPaired || this.deploymentSwitch.isLocal;

  public readonly deploymentVariants = this.deploymentSwitch.isPaired
    ? DEPLOYMENT_OPTIONS
    : DEPLOYMENT_OPTIONS.map((option) => ({ ...option, tooltip: DEPLOYMENT_INERT_TOOLTIP }));

  public onSwitchDeployment(variant: DeploymentVariant): void {
    this.deploymentSwitch.switchTo(variant);
  }

  public results$: Observable<SearchResult> = this.store.select(SearchSelectors.results);
  public searchLoading$: Observable<boolean> = this.store.select(SearchSelectors.loading);

  public deals$: Observable<Transaction[]> = this.store.select(DealComparisonSelectors.basket);
  public comparisonLoading$: Observable<boolean> = this.store.select(DealComparisonSelectors.loading);
  public impersonatedTenant$: Observable<string> = this.store.select(UserProfileSelectors.impersonatedTenant);
  public onboardingTenant$: Observable<boolean> = this.store.select(UserProfileSelectors.onboardingTenant);
  public impersonatedUser$: Observable<string> = this.store.select(UserProfileSelectors.impersonatedUser);
  public isReadOnlyImpersonation$: Observable<boolean> = this.store.select(UserProfileSelectors.isReadOnlyImpersonation);
  public notSupportedBrowser: boolean;
  public verifiedUser$ = this.permissionService.verified$;

  public transactionLookup$: Observable<Record<string, UnifiedSearchResult<Transaction>[]>>;

  public xRayOptions$: Observable<XRayOptions[]> = this.store.select(DocumentViewerSelectors.xRayOptions);

  public blocked$: Observable<boolean> = this.store.select(UserProfileSelectors.profile).pipe(map((profile) => profile.blocked));

  public readonly searchBoxComponent = viewChild(SearchBoxComponent);
  public readonly dealsIndicatorComponent = viewChild(DealsIndicatorComponent);

  private readonly panelMapper: Record<PanelTarget, () => IPanelComponent> = {
    Basket: () => this.dealsIndicatorComponent(),
    SearchBox: () => this.searchBoxComponent(),
  };

  public readonly SemerisRoutes = SemerisRoutes;

  public readonly avatarSize$ = this.store.select(AppSelectors.breakPointId).pipe(
    distinctUntilChanged(),
    map((bp) => (bp === 'small' || bp === 'extra-small' ? 5 : 8)),
  );

  private destructorSubject$ = new Subject<void>();

  public onRunCsvReport() {
    this.adminService
      .runCSVReport()
      .pipe(take(1))
      .subscribe((res) => {
        this.store.dispatch(
          AppActions.PushNotification({
            message: res.isSucceeded
              ? 'You will receive a slack message when the CSV report is ready.'
              : res.errorMessage || 'Failed to generate CSV report',
            config: res.isSucceeded ? 'success' : 'fail',
          }),
        );
      });
  }

  public onRunOrphanDealCleaner() {
    this.dialog.open<OrphanDataCleanerModalComponent>(OrphanDataCleanerModalComponent, {
      panelClass: 'orphan-data-cleaner-modal',
    });
  }

  public ngOnInit(): void {
    this.userInfo$ = this.authService.user$;
    this.notSupportedBrowser = !LocalStorageService.get<boolean>('browser-supported');

    combineLatest([
      this.permissionService.hasEveryPermission([Permission.READ_GUIDED_TOUR]),
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    ])
      .pipe(
        filter(([hasPermission]) => !!hasPermission),
        map(() => this.activatedRoute.snapshot.queryParamMap.get('guidedTourId')),
        distinctUntilChanged(),
        filter((guidedTourId) => !!guidedTourId),
        takeUntil(this.destructorSubject$),
      )
      .subscribe((guidedTourId) => {
        this.store.dispatch(
          AppActions.ToggleHelp({
            open: false,
          }),
        );
        this.store.dispatch(AppActions.StartGuidedTour({ guidedTourId }));
      });

    this.applicationService.panelCommandTrigger$
      .pipe(
        takeUntil(this.destructorSubject$),
        filter((command) => !!this.panelMapper[command.target] && !!this.panelMapper[command.target]()),
      )
      .subscribe((command) => {
        (this.panelMapper[command.target]() as IPanelComponent).handleCommand(command);
      });

    this.transactionLookup$ = this.store.select(TransactionSelectors.list).pipe(
      map((transactions) =>
        (transactions || []).reduce<Record<string, UnifiedSearchResult<Transaction>[]>>((acc, transaction) => {
          if (transaction?.transactionShortName) {
            pushToRecord<UnifiedSearchResult>(acc, transaction.transactionShortName.toLowerCase(), {
              routerLink: TransactionToLinkPipe.map(transaction),
              title: transaction.transactionDisplayName,
            });
          }
          if (transaction?.transactionId) {
            pushToRecord<UnifiedSearchResult>(acc, transaction.transactionId.toLowerCase(), {
              routerLink: TransactionToLinkPipe.map(transaction),
              title: transaction.transactionDisplayName,
            });
          }

          return acc;
        }, {}),
      ),
    );
  }

  public onExitImpersonation() {
    this.store
      .select(UserProfileSelectors.profile)
      .pipe(take(1))
      .subscribe((profile) => {
        this.store.dispatch(
          UserProfileActions.UpdateProfile({
            profile: { ...profile, impersonatedTenant: null, impersonatedUser: null },
          }),
        );
      });
  }

  public onMarketCompare(column: Column) {
    this.router.navigate(['/', SemerisRoutes.marketComparison.root], {
      queryParams: flattenObject({ columns: [DEFAULT_COLUMN, column], mode: DealFilterMode.All }),
    });
  }

  public onToggleTopic(topicsId: TopicId) {
    this.store.dispatch(ChecklistActions.ToggleTopic({ topicsId }));
  }

  public onResetSearch() {
    this.store.dispatch(SearchActions.Clear());
  }

  public onAddToCompare(transactionId: TransactionId) {
    this.store.dispatch(DealComparisonActions.AddToBasket({ transactionIds: [transactionId] }));
  }

  public onRemoveFromComparison(transactionId: TransactionId) {
    this.store.dispatch(DealComparisonActions.RemoveFromBasket({ transactionId }));
  }

  public onClear() {
    this.store.dispatch(DealComparisonActions.ClearBasket());
  }

  public logout() {
    this.store.dispatch(AppActions.Logout());
  }

  public search(input: string) {
    this.store.dispatch(SearchActions.Search({ filter: input }));
  }

  public addExamples() {
    this.store.dispatch(
      DealComparisonActions.AddToBasket({
        transactionIds: ['euclo.marlaypark', 'euclo.suttonpark'],
      }),
    );
  }

  public onFeedback() {
    this.store.dispatch(AppActions.OpenFeedback({ ...FEEDBACK_TEMPLATE_MAP.get('feedback') }));
  }

  public onXRayOptionsChange(ev: XRayOptionsChangedEvent) {
    this.store.dispatch(DocumentViewerActions.SetXRayOptions({ options: ev.current, save: true }));
  }

  public onToggleConversationSidebar() {
    this.store.dispatch(AppActions.ToggleConversationSidebar({}));
  }

  public onToggleGuidedTourSidebar() {
    this.store.dispatch(AppActions.ToggleHelp({ open: true, helpMode: HelpComponentMode.GuidedTours }));
  }

  public ngOnDestroy(): void {
    this.destructorSubject$.next(undefined);
    this.destructorSubject$.complete();
  }
}
